import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '$lib/lib/cn';
import { formatKrPerSqm } from '$lib/lib/formatters';
import type { ComparableDTO, ComparablesAggregate, ComparablesTarget } from '$lib/api/types';

// Histogram of comparable kr/m² values with median, p25/p75 bands, and a
// target marker. Pure SVG so it shares aesthetic with Sparkline and doesn't
// drag recharts onto the comparables panel — recharts already costs us a
// 100kb+ chunk on the price-history view (lazy-loaded there).

export interface ScoreHistogramProps {
  comparables: ComparableDTO[];
  aggregate: ComparablesAggregate;
  target?: ComparablesTarget;
  className?: string;
  /** When true, dims the chart to signal a recompute is in flight (decision 10). */
  stale?: boolean;
}

const BIN_COUNT = 16;
const HEIGHT = 80;
const PAD_X = 6;

function buildBins(values: number[], min: number, max: number) {
  if (values.length === 0 || max === min) return Array(BIN_COUNT).fill(0);
  const span = max - min;
  const bins = Array<number>(BIN_COUNT).fill(0);
  for (const v of values) {
    const t = (v - min) / span;
    const idx = Math.min(BIN_COUNT - 1, Math.max(0, Math.floor(t * BIN_COUNT)));
    bins[idx] += 1;
  }
  return bins;
}

export function ScoreHistogram({ comparables, aggregate, target, className, stale }: ScoreHistogramProps) {
  const id = useId();
  const { t } = useTranslation();
  const { bins, peakCount, scaleX, width } = useMemo(() => {
    const w = 360;
    const b = buildBins(
      comparables.map((c) => c.krPerSqm),
      aggregate.min || 0,
      aggregate.max || 0
    );
    const peak = b.reduce((m, x) => (x > m ? x : m), 0) || 1;
    const sx = (px: number) => {
      if (aggregate.max === aggregate.min) return PAD_X + (w - 2 * PAD_X) / 2;
      const t = (px - aggregate.min) / (aggregate.max - aggregate.min);
      return PAD_X + Math.max(0, Math.min(1, t)) * (w - 2 * PAD_X);
    };
    return { bins: b, peakCount: peak, scaleX: sx, width: w };
  }, [comparables, aggregate]);

  if (comparables.length === 0) {
    return (
      <div
        className={cn(
          'flex h-[120px] items-center justify-center rounded-sm border border-dashed border-rule bg-surface-2 text-2xs text-fg-muted',
          className
        )}
        role="status"
      >
        {t('comparables.histogramEmpty')}
      </div>
    );
  }

  const innerW = width - PAD_X * 2;
  const binWidth = innerW / BIN_COUNT;
  const targetX = target ? scaleX(target.krPerSqm) : null;
  const median = aggregate.median;
  const p25X = scaleX(aggregate.p25);
  const p75X = scaleX(aggregate.p75);
  const medianX = scaleX(median);

  return (
    <figure
      data-testid="score-histogram"
      aria-label={t('comparables.histogramAriaLabel', {
        count: comparables.length,
        median: formatKrPerSqm(median),
        p25: formatKrPerSqm(aggregate.p25),
        p75: formatKrPerSqm(aggregate.p75)
      })}
      className={cn('w-full', stale && 'opacity-60', className)}
    >
      <svg
        viewBox={`0 0 ${width} ${HEIGHT + 18}`}
        width="100%"
        preserveAspectRatio="none"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`hist-fill-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(var(--color-accent))" stopOpacity="0.55" />
            <stop offset="100%" stopColor="rgb(var(--color-accent))" stopOpacity="0.18" />
          </linearGradient>
        </defs>

        {/* Inter-quartile band */}
        <rect
          x={p25X}
          y={0}
          width={Math.max(2, p75X - p25X)}
          height={HEIGHT}
          fill="rgb(var(--color-accent))"
          opacity={0.08}
        />

        {/* Bars */}
        {bins.map((count, i) => {
          const h = count === 0 ? 0 : (count / peakCount) * (HEIGHT - 6);
          const x = PAD_X + i * binWidth + 0.5;
          const y = HEIGHT - h;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={Math.max(1, binWidth - 1)}
              height={h}
              fill={`url(#hist-fill-${id})`}
              stroke="rgb(var(--color-accent))"
              strokeOpacity={0.5}
              strokeWidth={0.5}
            />
          );
        })}

        {/* Baseline */}
        <line x1={0} x2={width} y1={HEIGHT} y2={HEIGHT} stroke="rgb(var(--color-rule))" strokeWidth={1} />

        {/* Percentile and median lines */}
        <line
          x1={p25X}
          x2={p25X}
          y1={0}
          y2={HEIGHT}
          stroke="rgb(var(--color-fg-subtle))"
          strokeDasharray="2 2"
          strokeWidth={1}
        />
        <line
          x1={p75X}
          x2={p75X}
          y1={0}
          y2={HEIGHT}
          stroke="rgb(var(--color-fg-subtle))"
          strokeDasharray="2 2"
          strokeWidth={1}
        />
        <line
          x1={medianX}
          x2={medianX}
          y1={0}
          y2={HEIGHT}
          stroke="rgb(var(--color-accent))"
          strokeWidth={1.4}
        />

        {/* Target marker */}
        {targetX != null && (
          <g>
            <line
              x1={targetX}
              x2={targetX}
              y1={0}
              y2={HEIGHT}
              stroke="rgb(var(--color-warning))"
              strokeWidth={1.4}
            />
            <polygon
              points={`${targetX - 4},${HEIGHT + 1} ${targetX + 4},${HEIGHT + 1} ${targetX},${HEIGHT - 4}`}
              fill="rgb(var(--color-warning))"
            />
          </g>
        )}

        {/* X-axis tick labels */}
        <text x={PAD_X} y={HEIGHT + 14} fontSize={10} fill="rgb(var(--color-fg-subtle))">
          {formatKrPerSqm(aggregate.min)}
        </text>
        <text x={width - PAD_X} y={HEIGHT + 14} fontSize={10} fill="rgb(var(--color-fg-subtle))" textAnchor="end">
          {formatKrPerSqm(aggregate.max)}
        </text>
      </svg>
      <figcaption className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-fg-muted">
        <span className="flex items-center gap-1">
          <span aria-hidden className="h-0.5 w-3 bg-accent" />
          {t('comparables.histogramLegendMedian')} <span className="num text-fg">{formatKrPerSqm(median)}</span>
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden className="h-2 w-2 rounded-xs bg-accent/20 ring-1 ring-accent/40" />
          P25–P75{' '}
          <span className="num text-fg">
            {formatKrPerSqm(aggregate.p25)}–{formatKrPerSqm(aggregate.p75)}
          </span>
        </span>
        {target && (
          <span className="flex items-center gap-1">
            <span aria-hidden className="h-0.5 w-3 bg-warning" />
            {t('comparables.histogramLegendTarget')} <span className="num text-fg">{formatKrPerSqm(target.krPerSqm)}</span>
          </span>
        )}
        <span className="ml-auto text-fg-subtle">
          <span className="num text-fg">{comparables.length}</span> {t('comparables.histogramLegendCount')}
        </span>
      </figcaption>
    </figure>
  );
}
